import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";
import axios from "axios";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const FormTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const LogoPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 4px;
  background: white;
`;

const PreviewText = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 0.75rem 2rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Props {
  setSnackbar: any;
}

const CreateClient: React.FC<Props> = ({ setSnackbar }) => {
  const navigate = useNavigate();
  const [clientName, setClientName] = useState("");
  const [phone, setPhone] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [status, setStatus] = useState("Active");
  const [description, setDescription] = useState("");
  const [tagline, setTagline] = useState("");
  const [verticalId, setVerticalId] = useState("");
  const [verticals, setVerticals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingVerticals, setLoadingVerticals] = useState(true);
  const token = localStorage.getItem("token");
  const [paidBy, setPaidBy] = useState("");
  const [amount, setAmount] = useState("");
  const [trafficPricing, setTrafficPricing] = useState<Record<string, string>>({
    email: "",
    social: "",
    sms: "",
    listicle: "",
    search: "",
    host_and_post: "",
  });

  const formatCurrencyInput = (value: string) => {
    // Keep digits + a single dot, max 2 decimals
    const cleaned = value.replace(/[^0-9.]/g, "");
    const [intPart, decPart] = cleaned.split(".");
    const intSafe = intPart ?? "";
    const decSafe = decPart ? decPart.slice(0, 2) : "";
    return decPart !== undefined ? `${intSafe}.${decSafe}` : intSafe;
  };

  const formatPercentInput = (value: string) => {
    // Keep digits + a single dot, max 2 decimals
    const cleaned = value.replace(/[^0-9.]/g, "");
    const [intPart, decPart] = cleaned.split(".");
    const intSafe = intPart ?? "";
    const decSafe = decPart ? decPart.slice(0, 2) : "";
    return decPart !== undefined ? `${intSafe}.${decSafe}` : intSafe;
  };

  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        setLoadingVerticals(true);
        const response = await axios.get(API_ENDPOINTS.VERTICALS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setVerticals(response.data || []);
      } catch (error) {
        console.error("Error fetching verticals:", error);
        setSnackbar({
          isOpen: true,
          message: "Failed to load verticals",
          type: "error",
        });
      } finally {
        setLoadingVerticals(false);
      }
    };

    fetchVerticals();
  }, [token, setSnackbar]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (!tagline.trim()) {
      setSnackbar({
        isOpen: true,
        message: "Tagline is required",
        type: "error",
      });
      setLoading(false);
      return;
    }

    if (!description.trim()) {
      setSnackbar({
        isOpen: true,
        message: "Description is required",
        type: "error",
      });
      setLoading(false);
      return;
    }

    if (!paidBy) {
      setSnackbar({
        isOpen: true,
        message: "Paid By is required",
        type: "error",
      });
      setLoading(false);
      return;
    }

    if (paidBy === "CPL") {
      const entries = Object.entries(trafficPricing)
        .map(([traffic_type, v]) => ({
          traffic_type,
          amount: v.trim(),
        }))
        .filter((x) => x.amount.length > 0);

      if (entries.length === 0) {
        setSnackbar({
          isOpen: true,
          message: "Please enter at least one traffic pricing amount",
          type: "error",
        });
        setLoading(false);
        return;
      }
    } else {
      if (!amount.trim()) {
        setSnackbar({
          isOpen: true,
          message: "Amount is required",
          type: "error",
        });
        setLoading(false);
        return;
      }
    }

    if (!logoFile) {
      setSnackbar({
        isOpen: true,
        message: "Logo is required",
        type: "error",
      });
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", clientName);
    formData.append("phone", phone.trim());
    formData.append("status", status === "Active" ? "1" : "0");
    formData.append("description", description);
    formData.append("tagline", tagline);
    if (verticalId) {
      formData.append("vertical_id", verticalId);
    }
    formData.append("logo", logoFile);
    formData.append("settlement_type", paidBy.toLowerCase());

    if (paidBy === "CPL") {
      const traffic_pricing = Object.entries(trafficPricing)
        .map(([traffic_type, v]) => ({
          traffic_type,
          amount: Number(v.trim()),
        }))
        .filter((x) => Number.isFinite(x.amount));
      traffic_pricing.forEach((tp, idx) => {
        formData.append(`traffic_pricing[${idx}][traffic_type]`, tp.traffic_type);
        formData.append(`traffic_pricing[${idx}][amount]`, tp.amount.toFixed(2));
      });
    } else {
      formData.append("amount", amount.trim());
    }

    try {
      await axios.post(API_ENDPOINTS.CLIENTS, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSnackbar({
        isOpen: true,
        message: "Client created successfully",
        type: "success",
      });
      navigate("/clients");
      setLoading(false);
    } catch (e: any) {
      console.error("Error creating client:", e);
      setSnackbar({
        isOpen: true,
        message: "Failed to create client. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <PageTitle>Create Client</PageTitle>
      <GoBack onClick={() => navigate("/clients")}>← Back to Clients</GoBack>

      <FormContainer>
        <FormTile>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="clientName">Client Name *</Label>
              <Input
                type="text"
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="vertical">Vertical (optional)</Label>
              <Select
                id="vertical"
                value={verticalId}
                onChange={(e) => setVerticalId(e.target.value)}
                disabled={loadingVerticals}
              >
                <option value="">
                  {loadingVerticals
                    ? "Loading verticals..."
                    : "Select a vertical"}
                </option>
                {verticals.map((vertical) => (
                  <option key={vertical.id} value={vertical.id}>
                    {vertical.name
                      ? vertical.name.charAt(0).toUpperCase() +
                        vertical.name.slice(1)
                      : `Vertical ${vertical.id}`}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tagline">Tagline *</Label>
              <Input
                type="text"
                id="tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Enter tagline"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="logo">Logo *</Label>
              <Input
                type="file"
                id="logo"
                accept="image/*"
                onChange={handleLogoChange}
                required
              />
              {logoPreview && (
                <LogoPreview>
                  <LogoImage src={logoPreview} alt="Logo preview" />
                  <PreviewText>Logo Preview</PreviewText>
                </LogoPreview>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="status">Status *</Label>
              <Select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="paidBy">Paid By *</Label>
              <Select
                id="paidBy"
                value={paidBy}
                onChange={(e) => {
                  setPaidBy(e.target.value);
                  setAmount("");
                  setTrafficPricing({
                    email: "",
                    social: "",
                    sms: "",
                    listicle: "",
                    search: "",
                    host_and_post: "",
                  });
                }}
                required
              >
                <option disabled value="">
                  Select one
                </option>
                <option value="CPL">CPL</option>
                <option value="CPM">COM</option>
                <option value="APPT">APPT</option>
              </Select>
            </FormGroup>

            {paidBy === "CPL" && (
              <FormGroup>
                <Label>Traffic Pricing</Label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
                    gap: "12px 16px",
                  }}
                >
                  {(
                    [
                      ["email", "Email"],
                      ["social", "Social"],
                      ["sms", "SMS"],
                      ["listicle", "Listicle"],
                      ["search", "Search"],
                      ["host_and_post", "Host & Post"],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Label htmlFor={`traffic-${key}`}>{label}</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        id={`traffic-${key}`}
                        value={trafficPricing[key] ? `$${trafficPricing[key]}` : ""}
                        onChange={(e) => {
                          const next = e.target.value.replace(/[$]/g, "");
                          setTrafficPricing((prev) => ({
                            ...prev,
                            [key]: formatCurrencyInput(next),
                          }));
                        }}
                        placeholder="$0.00"
                      />
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}

            {paidBy && paidBy !== "CPL" && (
              <FormGroup>
                <Label htmlFor="amount">
                  {paidBy === "CPM" ? "Amount (%) *" : "Amount *"}
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  id="amount"
                  value={
                    amount
                      ? paidBy === "CPM"
                        ? `${amount}%`
                        : `$${amount}`
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw.replace(/[$%]/g, "");
                    setAmount(
                      paidBy === "CPM"
                        ? formatPercentInput(next)
                        : formatCurrencyInput(next),
                    );
                  }}
                  placeholder={paidBy === "CPM" ? "0.00%" : "$0.00"}
                  required
                />
              </FormGroup>
            )}

            <ButtonGroup>
              <Button
                type="button"
                $variant="secondary"
                onClick={() => navigate("/clients")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Client"}
              </Button>
            </ButtonGroup>
          </form>
        </FormTile>
      </FormContainer>
    </Container>
  );
};

export default CreateClient;
